
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { CompanyData } from '@/types';
import { StandardizedConnection } from '@/types/connections';
import { Json } from '@/integrations/supabase/types';

/**
 * Normalize a connection object to canonical DB format,
 * stripping UI-only fields (logoUrl, metadata, description, setup_state, etc.).
 */
export const normalizeConnectionForStorage = (conn: any) => {
  const isCsv = conn.type === 'csv' || conn.import_type === 'csv' ||
    (conn.id && String(conn.id).startsWith('csv-import-')) ||
    (conn.connectorId && String(conn.connectorId).startsWith('csv-import-')) ||
    conn.service === 'csv';

  // Self-heal: infer missing fields for non-CSV (Fivetran) connections
  const service = conn.service || (!isCsv ? (conn.type || undefined) : undefined);
  const connectorId = conn.connectorId || (!isCsv ? (conn.id || undefined) : undefined);

  return {
    id: conn.id,
    name: conn.name || 'Data Connection',
    type: conn.type || conn.service || 'other',
    status: conn.status === 'active' ? 'active' : (conn.status || 'connected'),
    created_at: conn.created_at || new Date().toISOString(),
    connectionId: conn.connectionId || null,
    ...(service && { service }),
    ...(connectorId && { connectorId }),
    ...(conn.csvImports && { csvImports: conn.csvImports }),
    ...(conn.import_type && { import_type: conn.import_type }),
    ...(conn.ecommerce_subtype && { ecommerce_subtype: conn.ecommerce_subtype }),
    ...(conn.file_name && { file_name: conn.file_name }),
    ...((conn.metadata?.snapshot_connection_id || conn.metadata?.inventory_date || conn.metadata?.inventory_source || conn.metadata?.snapshot_date) && {
      metadata: {
        ...(conn.metadata?.snapshot_connection_id && { snapshot_connection_id: conn.metadata.snapshot_connection_id }),
        ...(conn.metadata?.inventory_date && { inventory_date: conn.metadata.inventory_date }),
        ...(conn.metadata?.inventory_source && { inventory_source: conn.metadata.inventory_source }),
        ...(conn.metadata?.snapshot_date && { snapshot_date: conn.metadata.snapshot_date }),
      }
    }),
  };
};

const dedupeInventorySnapshotConnections = (connections: any[]) => {
  const seenSnapshotSlices = new Set<string>();
  const deduped: any[] = [];

  // Walk from newest to oldest so the latest upload wins.
  for (let i = connections.length - 1; i >= 0; i--) {
    const conn = connections[i];
    const type = String(conn?.type || '').toLowerCase();
    const service = String(conn?.service || '').toLowerCase();
    const importType = String(conn?.import_type || '').toLowerCase();
    const subtype = String(conn?.ecommerce_subtype || '').toLowerCase();
    const snapshotDateRaw = conn?.metadata?.inventory_date || conn?.metadata?.snapshot_date || null;
    const snapshotDate = typeof snapshotDateRaw === 'string' ? snapshotDateRaw.slice(0, 10) : null;

    const isInventorySnapshotConnection =
      (type === 'csv' || service === 'csv') &&
      importType === 'ecommerce' &&
      subtype.length > 0 &&
      snapshotDate;

    if (!isInventorySnapshotConnection) {
      deduped.unshift(conn);
      continue;
    }

    const snapshotSlice = `${subtype}:${snapshotDate}`;
    if (seenSnapshotSlices.has(snapshotSlice)) {
      continue;
    }

    seenSnapshotSlices.add(snapshotSlice);
    deduped.unshift(conn);
  }

  return deduped;
};

export const saveCompanyAndConnections = async (
  companyData: CompanyData,
  connections: StandardizedConnection[],
  isDraft: boolean = false
): Promise<{ success: boolean; error?: string; organizationId?: string }> => {
  try {
    const safeConnections = Array.isArray(connections) ? connections : [];
    console.log('safeConnections', safeConnections);

    const enhancedConnections = safeConnections.map(connection => {
      if (!connection || !connection.id) {
        console.error('Invalid connection object:', connection);
        return connection;
      }

      const storedData = sessionStorage.getItem(`connection_${connection.id}`);
      let additionalDetails = {};

      if (storedData) {
        try {
          additionalDetails = JSON.parse(storedData);
        } catch (e) {
          console.error('Error parsing stored connection data:', e);
        }
      }

      return {
        ...connection,
        ...additionalDetails
      };
    });

    // Normalize connections to canonical format (strip UI-only fields)
    const normalizedConnections = enhancedConnections.map(normalizeConnectionForStorage);
    const cleanedConnections = dedupeInventorySnapshotConnections(normalizedConnections);

    if (!companyData.pocName || !companyData.pocEmail || !companyData.name) {
      throw new Error('Missing required company information');
    }

    const updatedCompanyData = {
      ...companyData,
      connections: cleanedConnections
    };

    const organizationData = {
      poc_name: updatedCompanyData.pocName,
      poc_email: updatedCompanyData.pocEmail,
      poc_phone: updatedCompanyData.pocPhone || null,
      poc_phone_verified: updatedCompanyData.pocPhoneVerified || false,
      name: updatedCompanyData.name,
      name_dbas: updatedCompanyData.name_dbas || null,
      address: updatedCompanyData.address || '',
      state_of_incorporation: updatedCompanyData.stateOfIncorporation || '',
      ein: updatedCompanyData.ein || '',
      products: updatedCompanyData.products as unknown as Json,
      connections: cleanedConnections as unknown as Json,
      revenue_last_12_months: updatedCompanyData.revenueLast12Months || null,
      status: isDraft ? 'draft' : 'submitted',
      user_id: companyData?.user_id || null,
      // Include id for upsert to work properly when updating existing organization
      ...(companyData.organization_id && { id: companyData.organization_id })
    };

    // Delegate final save to Edge Function (handles RLS and duplication checks)
    const { data, error } = await invokeFunction(supabase, 'organisations-manager-v2', {
      body: {
        action: 'finalize',
        data: organizationData,
        organization_id: companyData.organization_id || null,
      },
    });

    if (error) {
      console.error('Edge function error (finalize):', error);
      return { success: false, error: error.message };
    }

    if (!data?.success) {
      const message = data?.error || 'Failed to save organization';
      return { success: false, error: message };
    }

    const organizationId: string | undefined = data.organization_id;

    if (!isDraft) {
      sessionStorage.removeItem('figabl_connection_ids');
      sessionStorage.removeItem('figabl_csv_connection_ids');
      safeConnections.forEach(conn => {
        if (conn && conn.id) sessionStorage.removeItem(`connection_${conn.id}`);
      });
      sessionStorage.removeItem('selectedConnectorData');
    }

    return { success: true, organizationId };
  } catch (error) {
    console.error('Error saving company and connection data:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};

export const saveFormDraft = async (companyData: CompanyData): Promise<{ success: boolean; error?: string; data?: any }> => {
  try {
    const { data: session } = await supabase.auth.getSession();
    const userId = session?.session?.user?.id;

    if (!companyData.pocName || !companyData.pocEmail || !companyData.name) {
      throw new Error('Missing required company information');
    }

    const organizationData = {
      poc_name: companyData.pocName,
      poc_email: companyData.pocEmail,
      poc_phone: companyData.pocPhone || null,
      poc_phone_verified: companyData.pocPhoneVerified || false,
      name: companyData.name,
      name_dbas: companyData.name_dbas || null,
      address: companyData.address || '',
      state_of_incorporation: companyData.stateOfIncorporation || '',
      ein: companyData.ein || '',
      products: companyData.products,
      connections: Array.isArray(companyData.connections)
        ? companyData.connections.map(normalizeConnectionForStorage)
        : [],
      revenue_last_12_months: companyData.revenueLast12Months || null,
      user_id: userId || null
    };

    // Call the edge function instead of direct DB operations
    const { data, error } = await invokeFunction(supabase, 'organisations-manager-v2', {
      body: {
        action: 'save_draft',
        data: organizationData,
        organization_id: companyData.organization_id || null
      }
    });

    if (error) {
      console.error('Edge function error (save_draft):', error);
      return { success: false, error: error.message };
    }

    if (!data?.success) {
      const message = data?.error || 'Failed to save organization draft';
      return { success: false, error: message };
    }

    // Return the organization_id from the edge function response
    return {
      success: true,
      data: { id: data.organization_id }
    };
  } catch (error) {
    console.error('Error saving draft:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};
